import { useState } from 'react';

const DOCS = 'https://docs.sesame.software';

const SECTIONS = [
  {
    id: 'lca',
    title: 'Life Cycle Assessment (LCA)',
    docsUrl: `${DOCS}/methodology/pathway-analysis/emissions-accounting`,
    content: (
      <>
        <p>
          System boundary: <strong>well-to-gate</strong> — emissions are tracked from raw
          material extraction through the production gate of the primary product.
        </p>
        <p>
          Emissions are reported in CO₂-equivalent using{' '}
          <strong>100-year global warming potential (GWP100)</strong> factors.
        </p>
        <ul className="mt-1.5 space-y-0.5 pl-3 list-disc list-inside">
          <li><strong>Scope 1</strong> — direct process emissions (e.g. stack CO₂ from combustion)</li>
          <li><strong>Scope 2</strong> — emissions from purchased electricity and heat</li>
          <li><strong>Scope 3</strong> — upstream feedstock supply chains and transport</li>
        </ul>
      </>
    ),
  },
  {
    id: 'tea',
    title: 'Techno-Economic Analysis (TEA)',
    docsUrl: `${DOCS}/methodology/pathway-analysis/techno-economic-analysis/`,
    content: (
      <>
        <p>
          Costs are calculated using <strong>discounted cash flow (DCF)</strong> methodology
          and reported as a <strong>levelized cost</strong> of the primary product over the
          plant lifetime.
        </p>
        <p className="mt-1.5">
          Capital expenditure is annualized using a capital recovery factor derived from
          the project discount rate and plant life. Operating costs include feedstock,
          utilities, maintenance, and fixed overheads.
        </p>
      </>
    ),
  },
  {
    id: 'sensitivity',
    title: 'Sensitivity Analysis',
    docsUrl: `${DOCS}/methodology/pathway-analysis/mass-energy-balance`,
    content: (
      <p>
        Each parameter is varied independently across its full valid range (typically ±20%
        from the baseline, or the parameter's stated min/max), holding all other
        parameters at their baseline values. Results show which parameters have the
        largest impact on cost or emissions — the <strong>tornado chart</strong> ranks
        parameters by range of impact.
      </p>
    ),
  },
  {
    id: 'balance',
    title: 'Mass & Energy Balance',
    docsUrl: `${DOCS}/methodology/pathway-analysis/mass-energy-balance`,
    content: (
      <p>
        Starting from a single <strong>anchor</strong> (a pinned flow value — e.g. "produce
        1,000 kg H₂/day"), mass and energy values are propagated forward and backward
        through the pathway graph. Each node defines conversion ratios between its inputs
        and outputs, controlled by the node's parameters.
      </p>
    ),
  },
];

export default function MethodologyCard() {
  const [open, setOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  const toggle = id => setOpenSection(s => (s === id ? null : id));

  return (
    <div className="border border-stone-200 rounded-md overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-3 py-2 bg-stone-50 hover:bg-stone-100 transition-colors text-left"
        onClick={() => setOpen(o => !o)}
      >
        <span className="text-xs font-semibold text-slate-600">Methodology</span>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="normal-case font-normal">LCA · TEA · Sensitivity</span>
          <span>{open ? '▲' : '▼'}</span>
        </div>
      </button>

      {open && (
        <div className="divide-y divide-stone-100">
          {SECTIONS.map(section => (
            <div key={section.id}>
              <button
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-stone-50 transition-colors text-left"
                onClick={() => toggle(section.id)}
              >
                <span className="text-xs font-medium text-slate-700">{section.title}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={section.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={e => e.stopPropagation()}
                  >
                    docs ↗
                  </a>
                  <span className="text-xs text-slate-400">
                    {openSection === section.id ? '▲' : '▼'}
                  </span>
                </div>
              </button>
              {openSection === section.id && (
                <div className="px-3 pb-3 text-xs text-slate-600 leading-relaxed space-y-1.5">
                  {section.content}
                </div>
              )}
            </div>
          ))}

          <div className="px-3 py-2.5 flex items-center justify-between">
            <span className="text-[10px] text-slate-400">
              Sesame Pathway Analysis
            </span>
            <a
              href={`${DOCS}/methodology/pathway-analysis/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
            >
              Full methodology docs ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
